import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listTransactions } from "@/services/transactions.service";
import { getContract } from "@/services/contracts.service";
import { fmtMoney, fmtDateTime, statusColor } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowUpRight, Download } from "lucide-react";
import { FileDisputeModal } from "@/components/disputes/FileDisputeModal";
import { TransactionTypeIcon, txTypePretty } from "@/components/transactions/TransactionTypeIcon";
import { exportTransactionsCsv } from "@/components/transactions/transactionsCsv";
import { usePolling } from "@/lib/usePolling";

const Transactions = () => {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("out");
  const [contractFilter, setContractFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [disputeTarget, setDisputeTarget] = useState(null);
  const [eligibleMilestones, setEligibleMilestones] = useState({});

  const loadTxs = async () => {
    try { setTxs(await listTransactions()); } finally { setLoading(false); }
  };
  useEffect(() => { loadTxs(); }, []);
  usePolling(loadTxs);

  useEffect(() => {
    if (txs.length === 0) return;
    const contractIds = Array.from(new Set(txs.map((t) => t.contract_id).filter(Boolean)));
    (async () => {
      const map = {};
      await Promise.all(contractIds.map(async (cid) => {
        try {
          const c = await getContract(cid);
          c.milestones.forEach((m) => {
            if (["Funded", "Submitted", "Approved"].includes(m.status)) map[m.milestone_id] = true;
          });
        } catch {}
      }));
      setEligibleMilestones(map);
    })();
  }, [txs]);

  const filtered = useMemo(() => {
    return txs.filter((t) => {
      if (tab === "out" && t.direction !== "Out") return false;
      if (tab === "in" && t.direction !== "In") return false;
      if (contractFilter !== "all" && t.contract_id !== contractFilter) return false;
      if (statusFilter !== "all" && (t.status || "Complete") !== statusFilter) return false;
      if (typeFilter !== "all" && t.type !== typeFilter) return false;
      if (dateFrom && t.created_at < dateFrom) return false;
      if (dateTo && t.created_at > dateTo + "T23:59:59Z") return false;
      if (search && !(t.counterparty_name || "").toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [txs, tab, contractFilter, statusFilter, typeFilter, dateFrom, dateTo, search]);

  const contractOptions = useMemo(() => {
    const map = new Map();
    txs.forEach((t) => t.contract_id && !map.has(t.contract_id) && map.set(t.contract_id, t.contract_title || t.contract_id));
    return Array.from(map.entries());
  }, [txs]);

  const statusOptions = useMemo(() => Array.from(new Set(txs.map((t) => t.status || "Complete"))), [txs]);
  const typeOptions = useMemo(() => Array.from(new Set(txs.map((t) => t.type))), [txs]);

  const tabBtn = (key, label, testid) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${tab === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
      data-testid={testid}
    >
      {label}
    </button>
  );

  const totalAmount = filtered.reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Ledger</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Transactions</h1>
          <p className="mt-2 text-sm text-muted-foreground">Every funded escrow and released payment appears here automatically.</p>
        </div>
        <Button variant="outline" onClick={() => exportTransactionsCsv(filtered, `payd-transactions-${tab}.csv`)} disabled={filtered.length === 0} data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="inline-flex items-center gap-1 bg-secondary/50 border border-border rounded-lg p-1">
        {tabBtn("out", "Payments out", "tab-out-btn")}
        {tabBtn("in", "Payments in", "tab-in-btn")}
      </div>

      <div className="bg-card border border-border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">From</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 mt-1" data-testid="tx-filter-from" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">To</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 mt-1" data-testid="tx-filter-to" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Contract</label>
          <select value={contractFilter} onChange={(e) => setContractFilter(e.target.value)} className="h-9 mt-1 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="tx-filter-contract">
            <option value="all">All</option>
            {contractOptions.map(([id, title]) => (
              <option key={id} value={id}>{title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 mt-1 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="tx-filter-status">
            <option value="all">All</option>
            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 mt-1 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="tx-filter-type">
            <option value="all">All</option>
            {typeOptions.map((t) => <option key={t} value={t}>{txTypePretty(t)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Counterparty</label>
          <Input placeholder="Search name…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 mt-1" data-testid="tx-filter-counterparty" />
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="font-medium" data-testid="tx-empty">No transactions match these filters.</div>
            <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">Payments will appear here automatically after you fund a milestone or release payment.</p>
            <Link to="/contracts">
              <Button variant="outline" className="mt-4" data-testid="tx-empty-cta"><ArrowUpRight className="w-4 h-4 mr-1.5" /> Go to contracts</Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-muted-foreground text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Contract</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Milestone</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Counterparty</th>
                  <th className="text-left px-4 py-3 font-semibold">Dir</th>
                  <th className="text-left px-4 py-3 font-semibold">Type</th>
                  <th className="text-right px-4 py-3 font-semibold">Amount</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((t) => (
                  <tr key={t.tx_id} className="hover:bg-secondary/30" data-testid={`tx-row-${t.tx_id}`}>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDateTime(t.created_at)}</td>
                    <td className="px-4 py-3"><div className="font-medium truncate max-w-[200px]">{t.contract_title || "—"}</div></td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell"><div className="truncate max-w-[180px]">{t.milestone_title || "—"}</div></td>
                    <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell"><div className="truncate max-w-[180px]">{t.counterparty_name || "—"}</div></td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest font-bold ${t.direction === "In" ? "text-emerald-700" : "text-blue-700"}`}>{t.direction || "—"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <TransactionTypeIcon type={t.type} direction={t.direction} />
                        <span className="font-medium whitespace-nowrap">{txTypePretty(t.type)}</span>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-display font-semibold whitespace-nowrap ${t.direction === "In" ? "text-emerald-600" : ""}`}>
                      {t.direction === "In" ? "+" : "-"}{fmtMoney(t.amount, t.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor(t.status || "Complete")}`}>{t.status || "Complete"}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3 whitespace-nowrap">
                        {t.contract_id && (
                          <Link to={`/contracts/${t.contract_id}`} className="text-xs font-semibold text-primary hover:underline" data-testid={`tx-view-${t.tx_id}`}>View contract →</Link>
                        )}
                        {t.milestone_id && eligibleMilestones[t.milestone_id] && (
                          <button
                            type="button"
                            onClick={() => setDisputeTarget({ contractId: t.contract_id, milestoneId: t.milestone_id })}
                            className="text-xs font-semibold text-red-600 hover:underline"
                            data-testid={`tx-dispute-${t.tx_id}`}
                          >
                            File dispute
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-secondary/30 text-sm">
                  <td colSpan={6} className="px-4 py-3 text-muted-foreground">{filtered.length} transaction{filtered.length === 1 ? "" : "s"}</td>
                  <td className="px-4 py-3 text-right font-display font-bold">{fmtMoney(totalAmount)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <FileDisputeModal
        open={!!disputeTarget}
        onClose={() => setDisputeTarget(null)}
        prefillContractId={disputeTarget?.contractId}
        prefillMilestoneId={disputeTarget?.milestoneId}
        onFiled={loadTxs}
      />
    </div>
  );
};

export default Transactions;
